import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import companiesService from '../services/companiesService';
import importService from '../services/importService';

const isPreviewAuthBypassEnabled = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_TRONA_PREVIEW_AUTH_BYPASS === 'true';

const TRONA_PREVIEW_COMPANIES = [
  {
    id: 'trona-preview',
    companyId: 'trona-preview-company',
    name: 'Dashboard',
    status: 'delivered',
    ein: '12-3456789',
    state: 'Wyoming',
    createdAt: '2026-05-11T00:00:00.000Z',
    formationDate: '2026-05-11T00:00:00.000Z',
    _raw: { source: 'preview' },
  },
];
// Query keys for cache management
export const COMPANIES_QUERY_KEY = 'user-companies';

const IMPORT_REVIEW_STATUSES = new Set([
  'draft',
  'documents_pending',
  'documents_uploaded',
  'under_review',
]);

const IMPORT_APPROVED_STATUSES = new Set([
  'approved',
  'ra_pending',
  'ra_paid',
  'ra_processing',
]);

function normalizeCompany(item) {
  return {
    id:
      item.company_registration_id ||
      item.companyRegistrationId ||
      item.registrationId ||
      item.id,
    companyId: item.company_id || item.companyId,
    name: item.company_name || item.companyName || item.name,
    status: item.current_stage || item.currentStage || item.status,
    dissolutionStatus:
      item.dissolution_status ||
      item.dissolutionStatus ||
      item.dissolution?.status,
    dissolutionDate:
      item.dissolution_date ||
      item.dissolutionDate ||
      item.dissolution?.date,
    ein: item.ein,
    state: item.formation_state || item.formationState || item.state,
    createdAt: item.created_at || item.createdAt,
    formationDate: item.formation_date || item.formationDate,
    _raw: item,
  };
}

function getNormalizedImportValue(item, ...keys) {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return undefined;
}

function mapImportStatusToDashboardStatus(importStatus, fallbackStatus) {
  const normalizedStatus = (importStatus || '').toLowerCase();

  if (IMPORT_REVIEW_STATUSES.has(normalizedStatus)) {
    return 'under_review';
  }

  if (IMPORT_APPROVED_STATUSES.has(normalizedStatus)) {
    return 'approved';
  }

  if (normalizedStatus === 'rejected' || normalizedStatus === 'cancelled') {
    return normalizedStatus;
  }

  if (normalizedStatus === 'completed') {
    return fallbackStatus || 'completed';
  }

  return fallbackStatus;
}

function normalizeImportedCompany(importItem) {
  const importId = getNormalizedImportValue(importItem, 'import_id', 'importId', 'id');
  const registrationId = getNormalizedImportValue(
    importItem,
    'registration_id',
    'registrationId'
  );

  if (!importId) {
    return null;
  }

  const importStatus = getNormalizedImportValue(
    importItem,
    'status',
    'import_status'
  );

  return {
    id: registrationId || importId,
    registrationId,
    companyId: getNormalizedImportValue(importItem, 'company_id', 'companyId'),
    name:
      getNormalizedImportValue(importItem, 'company_name', 'companyName', 'name') ||
      'Imported company',
    status: mapImportStatusToDashboardStatus(importStatus, 'under_review'),
    state: getNormalizedImportValue(
      importItem,
      'formation_state',
      'formationState',
      'state'
    ),
    createdAt: getNormalizedImportValue(importItem, 'created_at', 'createdAt'),
    formationDate: getNormalizedImportValue(
      importItem,
      'formation_date',
      'formationDate'
    ),
    importStatus,
    importId,
    importOnly:
      !registrationId &&
      !getNormalizedImportValue(importItem, 'company_id', 'companyId'),
    _raw: {
      source: 'import',
      import_summary: importItem,
    },
  };
}

function mergeImportedCompanies(companies, imports) {
  if (!imports.length) {
    return companies;
  }

  const importsByCompanyId = new Map();
  const importsByRegistrationId = new Map();

  for (const importItem of imports) {
    const companyId = getNormalizedImportValue(
      importItem,
      'company_id',
      'companyId'
    );
    const registrationId = getNormalizedImportValue(
      importItem,
      'registration_id',
      'registrationId'
    );

    if (companyId) {
      importsByCompanyId.set(companyId, importItem);
    }

    if (registrationId) {
      importsByRegistrationId.set(registrationId, importItem);
    }
  }

  const matchedImportIds = new Set();
  const mergedCompanies = companies.map((company) => {
    const matchingImport =
      importsByCompanyId.get(company.companyId) || importsByRegistrationId.get(company.id);

    if (!matchingImport) {
      return company;
    }

    const importStatus = getNormalizedImportValue(
      matchingImport,
      'status',
      'import_status'
    );
    const importId = getNormalizedImportValue(
      matchingImport,
      'import_id',
      'importId',
      'id'
    );

    if (importId) {
      matchedImportIds.add(importId);
    }

    return {
      ...company,
      status: mapImportStatusToDashboardStatus(importStatus, company.status),
      importStatus,
      importId,
      companyId:
        company.companyId ||
        getNormalizedImportValue(matchingImport, 'company_id', 'companyId'),
      id:
        company.id ||
        getNormalizedImportValue(
          matchingImport,
          'registration_id',
          'registrationId',
          'id'
        ),
      _raw: {
        ...(company._raw || {}),
        source: 'import',
        import_summary: matchingImport,
      },
    };
  });

  const existingCompanyIds = new Set(companies.map((company) => company.companyId).filter(Boolean));
  const existingRegistrationIds = new Set(companies.map((company) => company.id).filter(Boolean));

  const importedAdditions = imports
    .filter((importItem) => {
      const importId = getNormalizedImportValue(importItem, 'import_id', 'importId', 'id');
      const companyId = getNormalizedImportValue(importItem, 'company_id', 'companyId');
      const registrationId = getNormalizedImportValue(
        importItem,
        'registration_id',
        'registrationId'
      );

      if (!importId || matchedImportIds.has(importId)) {
        return false;
      }

      if (!companyId && !registrationId) {
        return false;
      }

      return !(
        (registrationId && existingRegistrationIds.has(registrationId)) ||
        (companyId && existingCompanyIds.has(companyId))
      );
    })
    .map((importItem) => normalizeImportedCompany(importItem))
    .filter(Boolean);

  return [...mergedCompanies, ...importedAdditions];
}

/**
 * Hook for fetching user's companies with automatic cache management
 * @returns {Object} Query result with companies data and refresh function
 */
export const useUserCompanies = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const previewAuthBypass = isPreviewAuthBypassEnabled();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [COMPANIES_QUERY_KEY],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const [companyData, importsResult] = await Promise.all([
        companiesService.getUserCompanies(token),
        importService
          .getMyImports(token)
          .catch(() => ({ success: false, data: [] })),
      ]);
      const imports = Array.isArray(importsResult?.data)
        ? importsResult.data
        : [];

      return mergeImportedCompanies(
        companyData.map((item) => normalizeCompany(item)),
        imports
      );
    },
    enabled: isAuthenticated && !previewAuthBypass,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true,
  });

  // Function to manually refresh companies
  const refreshCompanies = () => {
    if (previewAuthBypass) return;
    queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
  };

  // Memoize empty array to prevent unnecessary re-renders
  const companies = useMemo(
    () => (previewAuthBypass ? TRONA_PREVIEW_COMPANIES : query.data || []),
    [previewAuthBypass, query.data]
  );

  return {
    ...query,
    companies,
    refreshCompanies,
  };
};

/**
 * Hook for creating initial company registration
 * Automatically invalidates companies cache on success
 * @returns {Object} Mutation object for creating registration
 */
export const useCreateRegistration = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const token = await getAccessTokenSilently();
      return companiesService.createInitialRegistration(data, token);
    },
    onSuccess: () => {
      // Invalidate companies cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
    },
  });
};

/**
 * Hook for submitting company details after payment
 * Automatically invalidates companies cache on success
 * @returns {Object} Mutation object for submitting details
 */
export const useSubmitCompanyDetails = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ registrationId, details }) => {
      const token = await getAccessTokenSilently();
      return companiesService.submitCompanyDetails(
        registrationId,
        details,
        token
      );
    },
    onSuccess: () => {
      // Invalidate companies cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
    },
  });
};

/**
 * Hook for fetching registration status
 * @param {string} registrationId - Registration UUID
 * @returns {Object} Query result with registration status
 */
export const useRegistrationStatus = (registrationId) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const previewAuthBypass = isPreviewAuthBypassEnabled();

  return useQuery({
    queryKey: ['registration-status', registrationId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return companiesService.getRegistrationStatus(registrationId, token);
    },
    enabled: isAuthenticated && !previewAuthBypass && !!registrationId,
    staleTime: 1000 * 30, // 30 seconds - status changes frequently
    refetchInterval: 1000 * 60, // Poll every minute for status updates
  });
};

/**
 * Hook for refreshing all company-related caches
 * @returns {Object} Functions to invalidate various caches
 */
export const useRefreshCompanies = () => {
  const queryClient = useQueryClient();

  return {
    refreshCompanies: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
    },
    refreshStatus: (registrationId) => {
      queryClient.invalidateQueries({
        queryKey: ['registration-status', registrationId],
      });
    },
    refreshAll: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['registration-status'] });
    },
  };
};

export default {
  useUserCompanies,
  useCreateRegistration,
  useSubmitCompanyDetails,
  useRegistrationStatus,
  useRefreshCompanies,
  COMPANIES_QUERY_KEY,
};
